import { groundOccupancyBaseSizeFt } from '../shared/teleport-placement';
import { encounterCreatureField, encounterField, hasCampaignPermission, hasPlayerPermission, posCoord } from '../shared/array-types';
import { _ftCamField } from '../shared/campaign-field';
import { teleportOfferIsCurrent, teleportOfferPositionMatches, teleportOfferContextMatches, teleportOfferPlayAllowed,
    type TeleportOffer as Offer, type TeleportOfferAgreement, type TeleportOfferIdentity as Identity } from '../janus-engine/movement/teleport-offer-currentness';
import { refuseUnported } from '../janus-engine/unported';
import { isRefusalCode } from '../janus-engine/telemetry/refusal-code';
import { withAsyncDeadline } from '../shared/async-deadline';

// Earned-teleport offers — PUSHED, not polled (pre-check handoff P6, board R-012, 2026-09-10).
//
// Earned offers arrive as stored facts on authenticated user:{id} channels. The acting browser
// publishes confirmed or declined offers through the shared truth transport so every resolver
// retires the same choice. The websocket router re-dispatches earned facts as `ft:teleport-offers`. A `list`
// read seeds the store only on a cold start: the active caster/encounter changing (page load included) and
// the user:{id} channel's subscription ack (first connect and every reconnect — frames missed before the
// channel was live). A failed seed backs off and then stops; no
// timer polls the endpoint.
//
// Ordering: both the push and `list` stamp `rev` BEFORE reading the offer hash, so a snapshot's rev is never
// later than a write it missed; the store keeps the newer rev (a push wins a tie).
//
// What the server does not push — expiry, the caster moving off the offer's origin, the turn moving on — is
// judged by the native currentness helper. Confirm saves the browser's landing
// verdict; a position/turn mismatch counts only after this browser's cache has
// agreed with the offer at least once (a cache that simply has not caught up with the offer yet proves nothing).

interface Held {
    rev: number;
    source: 'push' | 'list';
    offers: Offer[];
    skewS: number;
    /** Per offer: this browser's cache has matched its origin / its turn context at least once. */
    agreed: Map<string, TeleportOfferAgreement>;
}
interface Snapshot { rev?: number; server_now?: number; offers?: Offer[] }

const SEED_BACKOFF_MS = [5_000, 15_000, 60_000];
const store = new Map<string, Held>();
const seedFailures = new Map<string, { count: number; nextAt: number }>();
const storeKey = (encounterId: string, casterId: string) => `${encounterId.toUpperCase()}|${casterId.toUpperCase()}`;
const endpoint = () => BASE_URL + 'options/game/rolls/teleport_offer_inc.php';
const identity = (): Identity => ({ caster_id: String(window.ftGetActiveCasterId?.() ?? ''),
    encounter_id: String(window.ftGetActiveEncounterId?.() ?? '') });
const sameIdentity = (value: Identity) => JSON.stringify(value) === JSON.stringify(identity());
const campaignSceneMounted = () => !!document.querySelector('[data-ft3d-encounter-frame][src]');

let panel: HTMLElement | null = null;
let signature = '';
let seededFor = '';
let seedInFlight: string | null = null;
let reseedAfterFlight = false;
let panelBuilds = 0;

// Read-only diagnostics for smokes: how often the panel was rebuilt, and why each held offer shows or hides.
(window as Window & { __ftTeleportOffersDebug?: () => unknown }).__ftTeleportOffersDebug = () => {
    const context = identity();
    const held = store.get(storeKey(context.encounter_id, context.caster_id));
    return {
        panelBuilds, signature, seededFor, context,
        held: held ? { rev: held.rev, source: held.source, skewS: held.skewS } : null,
        offers: (held?.offers ?? []).map(offer => ({
            id: offer.id, expires: offer.expires, origin: offer.origin, context: offer.context, triggered: offer.triggered,
            position: teleportOfferPositionMatches(offer, casterRow(context.caster_id, context.encounter_id)),
            turn: teleportOfferContextMatches(offer, encounterRow(context.encounter_id)),
            agreed: held?.agreed.get(offer.id) ?? null,
            row: (() => { const row = casterRow(context.caster_id, context.encounter_id);
                return row ? ['x', 'y', 'z'].map(axis => posCoord(row, `${axis}_pos` as 'x_pos' | 'y_pos' | 'z_pos')) : null; })(),
        })),
    };
};

async function request(payload: Record<string, unknown>): Promise<Snapshot & { ok: boolean; message?: string; response: Response }> {
    const response = await withAsyncDeadline(() => fetch(endpoint(), { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload), signal: AbortSignal.timeout(12000) }), 12000);
    const result = await withAsyncDeadline(() => response.json(), 12000) as Snapshot & { ok: boolean; message?: string };
    if (!response.ok || !result.ok) throw new Error(result.message || 'Teleport could not be checked.');
    return { ...result, response };
}

/** Keep the NEWER of the held set and this one (server millisecond rev clock; a push wins a tie). */
function adopt(encounterId: string, casterId: string, snapshot: Snapshot, source: Held['source']): void {
    const key = storeKey(encounterId, casterId);
    const rev = Number(snapshot.rev ?? 0);
    const held = store.get(key);
    if (held && (rev < held.rev || (rev === held.rev && source === 'list'))) return;
    const serverNow = Number(snapshot.server_now ?? 0);
    const offers = Array.isArray(snapshot.offers) ? snapshot.offers : [];
    const agreed = new Map<string, TeleportOfferAgreement>();
    for (const offer of offers) agreed.set(offer.id, held?.agreed.get(offer.id) ?? { position: false, context: false });
    store.set(key, { rev, source, offers, skewS: serverNow > 0 ? serverNow - Date.now() / 1000 : 0, agreed });
}

type Row = Record<string, unknown>;
function encounterRow(encounterId: string): Row | undefined {
    const cache = window.FateTide?.encounterCache;
    return (cache?.get(encounterId) ?? cache?.get(encounterId.toUpperCase()) ?? cache?.get(encounterId.toLowerCase())) as Row | undefined;
}

function casterRow(casterId: string, encounterId: string): Row | null {
    const id = casterId.toUpperCase();
    const cache = window.FateTide?.casterCache as Map<string, unknown> | undefined;
    const cached = cache?.get(casterId) ?? cache?.get(id);
    if (cached) return cached as Row;
    const encounter = encounterRow(encounterId) as { encounter_creatures_arr?: Row[] } | undefined;
    return (encounter?.encounter_creatures_arr ?? []).find(row => {
        return String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase() === id;
    }) ?? null;
}

/** Match the caster's campaign context without importing another bundle's state. */
function playAllowed(context: Identity): boolean {
    const caster = casterRow(context.caster_id, context.encounter_id);
    const attached = encounterCreatureField(caster?.player_characters, 'attached_campaign_id');
    const encounter = encounterRow(context.encounter_id);
    const parent = encounterField(encounter, 'encounter_master_parent_type') === 'campaign'
        ? encounterField(encounter, 'encounter_master_parent_id') : '';
    const ft = window.FateTide as { campaignCache?: Map<string, Row>; currentCampaignArr?: Row } | undefined;
    const lookup = (id: string): Row | undefined =>
        ft?.campaignCache?.get(id) ?? ft?.campaignCache?.get(id.toUpperCase()) ?? ft?.campaignCache?.get(id.toLowerCase())
        ?? (_ftCamField(ft?.currentCampaignArr, 'campaign_id')?.toUpperCase() === id.toUpperCase() ? ft?.currentCampaignArr : undefined);
    const campaign = caster?.campaign_arr as Row | undefined
        ?? (attached ? lookup(attached) : undefined) ?? (parent ? lookup(parent) : undefined);
    return teleportOfferPlayAllowed(_ftCamField(campaign, 'campaign_ft3d_mode'), hasCampaignPermission(caster) || hasCampaignPermission(encounter));
}

function removePanel(): void { panel?.remove(); panel = null; signature = ''; }

function visibleOffers(context: Identity): Offer[] {
    const held = store.get(storeKey(context.encounter_id, context.caster_id));
    return held ? held.offers.filter(offer => {
        const agreed = held.agreed.get(offer.id) ?? { position: false, context: false };
        held.agreed.set(offer.id, agreed);
        return teleportOfferIsCurrent(offer, agreed, context, casterRow(context.caster_id, context.encounter_id),
            encounterRow(context.encounter_id), Date.now() / 1000 + held.skewS);
    }) : [];
}

/** Shared by Choose, the pre-draw planner entry, its async completion, and Decline. */
function resolutionError(context: Identity, offerId: string): string | null {
    if (!canResolve(context) || !visibleOffers(context).some(offer => offer.id === offerId))
        return 'The teleport choice changed while its landing was being prepared.';
    return playAllowed(context) ? null : 'The GM has paused play.';
}

function dropOffer(context: Identity, offerId: string): void {
    const held = store.get(storeKey(context.encounter_id, context.caster_id));
    if (held) held.offers = held.offers.filter(offer => offer.id !== offerId);
}

function offerSizeFt(context: Identity, offer: Offer): number {
    const caster = casterRow(context.caster_id, context.encounter_id);
    return caster ? groundOccupancyBaseSizeFt(ftEffectiveSizeId(caster) ?? ftLayerValue(caster, 'size'), SIZE_TYPES)
        : offer.sizeFt ?? groundOccupancyBaseSizeFt(null, SIZE_TYPES);
}

/** A reaction can earn a choice while somebody else is active. Its own controller (including the GM) chooses the landing. */
function canResolve(context: Identity): boolean {
    if (context.encounter_id.toUpperCase() !== identity().encounter_id.toUpperCase()) return false;
    const caster = casterRow(context.caster_id, context.encounter_id);
    return sameIdentity(context) || hasPlayerPermission(caster) || hasCampaignPermission(caster)
        || hasCampaignPermission(encounterRow(context.encounter_id));
}

function render(): void {
    const activeContext = identity();
    if (!activeContext.caster_id || !activeContext.encounter_id || !campaignSceneMounted()) { removePanel(); return; }
    const contexts = new Map<string, Identity>([[storeKey(activeContext.encounter_id, activeContext.caster_id), activeContext]]);
    for (const key of store.keys()) {
        const [encounter_id, caster_id] = key.split('|');
        if (encounter_id && caster_id) {
            const context = { encounter_id, caster_id };
            if (canResolve(context)) contexts.set(key, context);
        }
    }
    const offers = [...contexts.values()].flatMap(context => visibleOffers(context).map(offer => ({ context, offer })));
    const active = window.aoeManager?.ft3dActive as { teleportOfferId?: string } | null | undefined;
    if (active?.teleportOfferId && !offers.some(({ offer }) => offer.id === active.teleportOfferId)) {
        window.aoeManager?.cancelFt3d();
    }
    const next = JSON.stringify([activeContext, offers.map(({ context, offer: o }) => [context, o.id, o.name, o.rangeFt, offerSizeFt(context, o), o.abilityId])]);
    if (next === signature) return;
    removePanel(); signature = next;
    if (!offers.length) return;
    panelBuilds += 1;
    panel = document.createElement('aside');
    panel.dataset.ftTeleportOffers = '';
    panel.setAttribute('aria-label', 'Earned teleports');
    panel.style.cssText = 'position:fixed;right:16px;top:250px;z-index:20000;width:min(270px,calc(100vw - 32px));max-height:calc(100dvh - 280px);overflow:auto;padding:12px;background:#17232f;color:white;border:1px solid #d5bd77;border-radius:8px';
    for (const { context, offer } of offers) {
        const row = document.createElement('div');
        const label = document.createElement('p');
        const caster = casterRow(context.caster_id, context.encounter_id);
        const who = sameIdentity(context) ? '' : String(encounterCreatureField(caster?.encounter_creatures, 'encounter_creature_name')
            || (caster ? ftItemsCol(caster, 'item_name') : '') || 'Creature') + ' — ';
        label.textContent = `${who}${offer.name}: teleport up to ${offer.rangeFt} ft`;
        const choose = document.createElement('button'); choose.type = 'button';
        choose.className = 'btn btn-sm btn-outline-light me-2';
        choose.textContent = 'Choose destination'; choose.dataset.ftTeleportOfferChoose = offer.id;
        choose.addEventListener('click', () => {
            const error = resolutionError(context, offer.id);
            if (error) { displayWarning(error); render(); return; }
            window.ftStartTeleportPlacement?.({ casterId: context.caster_id, encounterId: context.encounter_id,
                abilityId: offer.abilityId, name: offer.name, rangeFt: offer.rangeFt, sizeFt: offerSizeFt(context, offer), offerId: offer.id,
                ...(sameIdentity(context) ? {} : { controllerId: activeContext.caster_id }),
                cast: point => {
                    if (choose.disabled || !sameIdentity(activeContext)) return;
                    const error = resolutionError(context, offer.id);
                    if (error) { displayWarning(error); render(); return; }
                    const plan = window.ftPlanTeleportOfferLanding;
                    if (!plan) { refuseUnported('Teleport landing support is not available yet.', undefined, undefined, 'teleport-offer:landing-support-unavailable'); return; }
                    choose.disabled = true;
                    void plan(context.encounter_id, context.caster_id, point, offer.rangeFt).then(settlement => {
                        if (!sameIdentity(activeContext)) throw new Error('The active creature changed while this teleport was being prepared.');
                        const error = resolutionError(context, offer.id);
                        if (error) throw new Error(error);
                        return request({ ...context, mode: 'confirm', offer_id: offer.id, settlement }).then(async result => {
                            await window.ftTeleportOfferLandingCommitted?.(context.encounter_id, settlement, offer.id, context.caster_id);
                            await window.ftSaveAppliedTruth?.(offer.id, async () => result.response);
                            dropOffer(context, offer.id);
                            if (settlement.fortunateOffers?.length) window.ftOfferFortunate?.(context.encounter_id, context.caster_id, settlement.fortunateOffers);
                        });
                    })
                        .catch(error => {
                            const reason = String(error?.reason ?? error?.refusal ?? error?.message ?? error).replace(/^(?:hand-back|refusal):\s*/, '');
                            if (reason === 'teleport-offer:destination-out-of-range')
                                displayWarning('That spot is out of reach — choose another.');
                            else if (reason === 'teleport-offer:no-clear-space')
                                displayWarning('There is no room to land there — choose another.');
                            else if (reason === 'teleport-offer:actor-moved-during-planning')
                                displayWarning('The creature moved while this teleport was being prepared. Choose the destination again.');
                            else if ((error?.reason || reason.startsWith('teleport-offer:') || String(error?.message).startsWith('hand-back:')) && isRefusalCode(reason.toLowerCase()))
                                refuseUnported('This teleport landing cannot be resolved yet.', undefined, undefined, `teleport-offer:landing-unresolved:${reason.toLowerCase()}`);
                            else displayWarning(reason);
                        })
                        .finally(() => { choose.disabled = false; render(); });
                },
            });
        });
        const decline = document.createElement('button'); decline.type = 'button'; decline.textContent = 'Decline';
        decline.className = 'btn btn-sm btn-outline-light';
        decline.dataset.ftTeleportOfferDecline = offer.id;
        decline.addEventListener('click', () => {
            const error = resolutionError(context, offer.id);
            if (error) { displayWarning(error); render(); return; }
            decline.disabled = true; choose.disabled = true;
            void request({ ...context, mode: 'decline', offer_id: offer.id })
                .then(async result => {
                    const command = { operation_id: offer.id, encounter_id: context.encounter_id, actor_id: context.caster_id };
                    await window.ftPushAppliedTruth?.({ type: 'janus_status_truth', command,
                        applied_frame: { kind: 'applied', ...command, rev: 0, member_revs: {}, rows: [], computed: {}, new_entities: [], deletes: [],
                            inputs: { teleport_offer_resolved: { ...context, offer_id: offer.id } } } });
                    await window.ftSaveAppliedTruth?.(offer.id, async () => result.response);
                    dropOffer(context, offer.id);
                })
                .catch(error => displayWarning(String(error.message)))
                .finally(() => { signature = ''; render(); });
        });
        row.append(label, choose, decline); panel.append(row);
    }
    document.body.append(panel);
}

/**
 * The caster stands in the ACTIVE encounter: its own cached row names that encounter, else the encounter's roster holds it.
 * The list endpoint refuses (403) a caster from another encounter, so the page never asks for one (N10 2026-10-03; N9 saw
 * the 403 when the active caster stood in a different encounter than the active one). Unknown = not proven = no read yet.
 */
function casterInActiveEncounter(context: Identity): boolean {
    const id = context.caster_id.toUpperCase(), encounterId = context.encounter_id.toUpperCase();
    const cache = window.FateTide?.casterCache as Map<string, unknown> | undefined;
    const cached = (cache?.get(context.caster_id) ?? cache?.get(id)) as { encounter_creatures?: { encounter_id?: unknown } } | undefined;
    const own = String(encounterCreatureField(cached?.encounter_creatures, 'encounter_id') ?? '').trim().toUpperCase();
    if (own) return own === encounterId;
    const encounters = window.FateTide?.encounterCache as Map<string, { encounter_creatures_arr?: unknown[] }> | undefined;
    const encounter = encounters?.get(context.encounter_id) ?? encounters?.get(encounterId) ?? encounters?.get(encounterId.toLowerCase());
    return (encounter?.encounter_creatures_arr ?? []).some(row => {
        const ec = (row as { encounter_creatures?: { encounter_creature_id?: unknown } } | null)?.encounter_creatures;
        return String(encounterCreatureField(ec, 'encounter_creature_id') ?? '').toUpperCase() === id;
    });
}

/** The one network read: the cold-start snapshot for the active caster. */
async function seed(force = false): Promise<void> {
    const context = identity();
    // Selection can outlive control permission, or precede its cold-cache adoption.
    if (!context.caster_id || !context.encounter_id || !campaignSceneMounted() || !casterInActiveEncounter(context)
        || !hasPlayerPermission(casterRow(context.caster_id, context.encounter_id))) { removePanel(); return; }
    const key = storeKey(context.encounter_id, context.caster_id);
    if (seedInFlight === key) { if (force) reseedAfterFlight = true; return; } // a reconnect mid-read reads again after it
    if (!force && seededFor === key) { render(); return; }
    seedInFlight = key;
    try {
        const snapshot = await request({ ...context, mode: 'list' });
        adopt(context.encounter_id, context.caster_id, snapshot, 'list');
        seededFor = key;
        seedFailures.delete(key);
    } catch {
        // Back off (5 s, 15 s, 60 s) and then stop: the next push, identity change or reconnect heals it.
        const failure = seedFailures.get(key) ?? { count: 0, nextAt: 0 };
        failure.count += 1;
        const delay = SEED_BACKOFF_MS[failure.count - 1];
        failure.nextAt = delay === undefined ? Number.POSITIVE_INFINITY : Date.now() + delay;
        seedFailures.set(key, failure);
    } finally {
        if (seedInFlight === key) seedInFlight = null;
    }
    render();
    if (reseedAfterFlight) { reseedAfterFlight = false; void seed(true); }
}

window.addEventListener('ft:truth-frame-applied', event => {
    const resolved = (event as CustomEvent<{ inputs?: { teleport_offer_resolved?: Identity & { offer_id: string } } }>).detail?.inputs?.teleport_offer_resolved;
    if (resolved) { dropOffer(resolved, resolved.offer_id); render(); }
});
window.addEventListener('ft:teleport-offers', event => {
    const frame = (event as CustomEvent<{ encounter_id?: string; caster_id?: string } & Snapshot>).detail;
    if (!frame?.encounter_id || !frame.caster_id) return;
    adopt(frame.encounter_id, frame.caster_id, frame, 'push');
    render();
});
window.addEventListener('ft:browser-teleport-offers', event => {
    const { encounterId, offers } = (event as CustomEvent<{ encounterId: string;
        offers: import('../janus-engine/ability/movement/ability-teleport').AbilityTeleportOffer[] }>).detail;
    for (const offer of offers) {
        const key = storeKey(encounterId, offer.casterId), held = store.get(key);
        const choices = new Map((held?.offers ?? []).map(row => [row.id, row]));
        choices.set(offer.id, { ...offer, caster_id: offer.casterId, encounter_id: encounterId });
        adopt(encounterId, offer.casterId, { rev: Math.max(Date.now(), (held?.rev ?? 0) + 1),
            server_now: Date.now() / 1000, offers: [...choices.values()] }, 'push');
    }
    render();
});
// Seed once the push channel is LIVE — on first connect and after every reconnect. Reading before the
// user:{id} ack could miss an offer pushed in between (the socket-open hook fires before re-subscription).
window.addEventListener('ft:ws-channel-subscribed', event => {
    const { channel, status } = (event as CustomEvent<{ channel?: string; status?: string }>).detail ?? {};
    if (status !== 'subscribed' || !String(channel ?? '').startsWith('user:')) return;
    seedFailures.clear();
    void seed(true);
});
// Local re-evaluation (expiry, moves, turn changes). The only network step is the cold-start seed when the
// active caster/encounter changes or the scene mounts, and its bounded retry after a failure.
window.addEventListener('ft:ws-state-frame', () => { render(); });
window.setInterval(() => {
    const context = identity();
    const key = context.caster_id && context.encounter_id ? storeKey(context.encounter_id, context.caster_id) : '';
    const failure = key ? seedFailures.get(key) : undefined;
    if (key && key !== seededFor && campaignSceneMounted() && casterInActiveEncounter(context) && (!failure || Date.now() >= failure.nextAt)) void seed();
    else render();
}, 1000);
